// Layout Components - Page Container with Cosmic Background
import React from 'react';
import { motion } from 'framer-motion';

// Page transition animations
const pageVariants = {
    initial: {
        opacity: 0,
        y: 20
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1]
        }
    },
    exit: {
        opacity: 0,
        y: -10,
        transition: {
            duration: 0.2
        }
    }
};

// Main page container with cosmic background
export const PageContainer = ({ children, className = '' }) => {
    return (
        <motion.div
            className={`w-full max-w-4xl mx-auto ${className}`}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            {children}
        </motion.div>
    );
};

// Animated background with nebula effects
export const CosmicBackground = () => {
    return (
        <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
            {/* Base gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#050508] via-[#0d0d1a] to-[#050508]" />

            {/* Nebula orbs */}
            <motion.div
                className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
                style={{
                    background: 'radial-gradient(circle, var(--nebula-1, rgba(147, 51, 234, 0.15)) 0%, transparent 70%)',
                    filter: 'blur(60px)',
                }}
                animate={{
                    x: [0, 30, -20, 0],
                    y: [0, -20, 30, 0],
                    scale: [1, 1.1, 0.95, 1],
                }}
                transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <motion.div
                className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full"
                style={{
                    background: 'radial-gradient(circle, var(--nebula-2, rgba(59, 130, 246, 0.12)) 0%, transparent 70%)',
                    filter: 'blur(50px)',
                }}
                animate={{
                    x: [0, -25, 15, 0],
                    y: [0, 25, -15, 0],
                    scale: [1, 0.95, 1.1, 1],
                }}
                transition={{
                    duration: 18,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                }}
            />

            <motion.div
                className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full"
                style={{
                    background: 'radial-gradient(circle, var(--nebula-3, rgba(236, 72, 153, 0.1)) 0%, transparent 70%)',
                    filter: 'blur(40px)',
                }}
                animate={{
                    x: [0, 15, -25, 0],
                    y: [0, -30, 10, 0],
                    scale: [1, 1.05, 0.9, 1],
                }}
                transition={{
                    duration: 22,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 5
                }}
            />

            {/* Accent glow - uses theme accent */}
            <motion.div
                className="absolute top-1/3 left-1/2 w-48 h-48 rounded-full"
                style={{
                    background: 'radial-gradient(circle, var(--theme-accent-glow, rgba(245, 197, 24, 0.08)) 0%, transparent 70%)',
                    filter: 'blur(30px)',
                }}
                animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            {/* Star particles */}
            <div className="absolute inset-0">
                {[...Array(30)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-white rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            opacity: [0.2, 0.8, 0.2],
                            scale: [1, 1.5, 1],
                        }}
                        transition={{
                            duration: 2 + Math.random() * 3,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

// Header component
export const Header = ({
    children,
    showBack = false,
    onBack,
    rightContent
}) => {
    return (
        <header className="fixed top-0 left-0 right-0 z-50 px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
                {/* Left side */}
                <div className="flex items-center gap-3">
                    {showBack && (
                        <motion.button
                            onClick={onBack}
                            className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-amber-200 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </motion.button>
                    )}
                    {children}
                </div>

                {/* Right side */}
                {rightContent && (
                    <div className="flex items-center gap-2">
                        {rightContent}
                    </div>
                )}
            </div>
        </header>
    );
};

// Footer component
export const Footer = ({ userId }) => {
    return (
        <footer className="mt-auto pt-8 pb-4 text-center">
            <p className="text-purple-400/60 text-xs">
                Cosmic Compass - Your guide in the digital universe.
            </p>
            {userId && (
                <p className="text-purple-400/40 text-[10px] mt-1">
                    User ID: {userId.substring(0, 8)}...
                </p>
            )}
        </footer>
    );
};

// Glass Card wrapper - uses theme variables
export const GlassCard = ({ children, className = '', onClick, hoverable = false }) => {
    return (
        <motion.div
            className={`
                backdrop-blur-md
                rounded-2xl
                p-4 sm:p-6
                ${hoverable ? 'cursor-pointer' : ''}
                ${className}
            `}
            style={{
                background: 'var(--theme-glass-bg)',
                border: '1px solid var(--theme-glass-border)',
            }}
            onClick={onClick}
            whileHover={hoverable ? {
                scale: 1.01,
                boxShadow: '0 0 20px var(--theme-card-glow)',
                transition: { duration: 0.2 }
            } : undefined}
        >
            {children}
        </motion.div>
    );
};

export default PageContainer;
